/**
 * FeatureRoute — Route-level gating by feature flag.
 *
 * Wraps a route element. If the required feature flag is disabled,
 * shows an UpgradePrompt instead of the page content.
 *
 * @example
 * <Route path="/crm" element={
 *   <FeatureRoute flag="crm_module">
 *     <CRMDashboard />
 *   </FeatureRoute>
 * } />
 */
import { Box, Button, Paper, Typography } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag, useFeatureFlagsContext } from '../../hooks/useFeatureFlag';

/**
 * Upgrade prompt shown when a module is disabled.
 */
function UpgradePrompt({
  featureKey,
  title = 'Модуль недоступен',
  message = 'Этот функционал отключён для вашей организации. Обратитесь к администратору или обновите тарифный план.',
  homeText = 'На главную',
  settingsText = 'Настройки',
}) {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          textAlign: 'center',
          p: { xs: 3, sm: 5 },
          maxWidth: 480,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <LockIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {message}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button variant="outlined" onClick={() => navigate('/dashboard')}>
            {homeText}
          </Button>
          <Button variant="contained" onClick={() => navigate('/settings')}>
            {settingsText}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

/**
 * FeatureRoute component
 * @param {Object} props
 * @param {string} props.flag - Feature flag key to check
 * @param {string[]} [props.anyOf] - Show if ANY of these flags is enabled
 * @param {React.ReactNode} props.children - Route content
 * @param {React.ReactNode} [props.fallback] - Custom fallback (default: UpgradePrompt)
 */
export default function FeatureRoute({ flag, anyOf, children, fallback, upgradeTitle, upgradeMessage, upgradeHomeText, upgradeSettingsText }) {
  // Single flag check
  const { isEnabled: singleEnabled, isLoading: singleLoading } =
    useFeatureFlag(flag || '_noop_');

  // If anyOf is provided, check if any flag is enabled
  // (we can't call hooks conditionally, so we always call useFeatureFlag for the first key)
  if (anyOf && anyOf.length > 0) {
    // anyOf mode — we use the provider context directly
    return (
      <AnyOfGate anyOf={anyOf} fallback={fallback} upgradeTitle={upgradeTitle} upgradeMessage={upgradeMessage} upgradeHomeText={upgradeHomeText} upgradeSettingsText={upgradeSettingsText}>
        {children}
      </AnyOfGate>
    );
  }

  if (!flag) return children;

  if (singleLoading) return null;

  if (!singleEnabled) {
    return fallback || <UpgradePrompt featureKey={flag} title={upgradeTitle} message={upgradeMessage} homeText={upgradeHomeText} settingsText={upgradeSettingsText} />;
  }

  return children;
}

/**
 * Internal component for anyOf checks (avoids conditional hook call)
 */
function AnyOfGate({ anyOf, fallback, children, upgradeTitle, upgradeMessage, upgradeHomeText, upgradeSettingsText }) {
  const { features, isLoading } = useFeatureFlagsContext();

  if (isLoading) return null;

  const anyEnabled = anyOf.some((key) => features[key]?.enabled);

  if (!anyEnabled) {
    return fallback || <UpgradePrompt featureKey={anyOf[0]} title={upgradeTitle} message={upgradeMessage} homeText={upgradeHomeText} settingsText={upgradeSettingsText} />;
  }

  return children;
}
